import * as _React from 'react'
import {useCallback, useEffect, useRef, useState} from 'react'
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native'
import {useSafeAreaInsets} from 'react-native-safe-area-context'
import {useNavigation, useRoute} from '@react-navigation/native'

import {useSession} from '#/state/session'
import {useSetMinimalShellMode} from '#/state/shell/minimal-mode'
import {JitsiGuestMeeting} from './components/JitsiGuestMeeting'
import {JitsiMeeting} from './components/JitsiMeeting'

function stripBskySuffix(handle: string): string {
  return handle.replace(/\.bsky\.social$/, '')
}

type Role = 'pending' | 'owner' | 'guest' | 'no-session'

export function PersonalRoomScreen() {
  const safeAreaInsets = useSafeAreaInsets()
  const route = useRoute<any>()
  const navigation = useNavigation<any>()
  const {currentAccount} = useSession()
  const setMinimalShell = useSetMinimalShellMode()

  const token = route.params?.token as string | undefined
  const isEntryRoute = !token
  const isAuthenticated = !!currentAccount?.handle

  const roomSlug = token ? `p2p-personal-${token}` : ''
  const [role, setRole] = useState<Role>('pending')
  const [ownerHandle, setOwnerHandle] = useState<string | null>(null)
  const [entryError, setEntryError] = useState<string | null>(null)
  const [roomEnded, setRoomEnded] = useState<
    false | 'owner_left' | 'guest_left'
  >(false)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const deactivatedRef = useRef(false)

  // /1o1 (no token): mint a fresh session and redirect to /1o1/r/<token>.
  useEffect(() => {
    if (!isEntryRoute) return
    if (!isAuthenticated || !currentAccount?.did) {
      setEntryError('Sign in to start a 1:1 room.')
      return
    }
    let cancelled = false
    fetch('/api/personal-room/start-session', {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({
        ownerDid: currentAccount.did,
        ownerHandle: currentAccount.handle,
      }),
    })
      .then(res => res.json())
      .then((data: {token?: string; error?: string}) => {
        if (cancelled) return
        if (!data.token) {
          setEntryError(data.error || 'Failed to start session.')
          return
        }
        navigation.replace('PersonalRoom', {token: data.token})
      })
      .catch(err => {
        if (cancelled) return
        console.error('start-session failed:', err)
        setEntryError('Failed to start session.')
      })
    return () => {
      cancelled = true
    }
  }, [
    isEntryRoute,
    isAuthenticated,
    currentAccount?.did,
    currentAccount?.handle,
    navigation,
  ])

  // /1o1/r/:token: resolve role by polling status. Owner = creator_did matches.
  useEffect(() => {
    if (isEntryRoute) return
    let cancelled = false
    const resolveRole = async () => {
      try {
        const res = await fetch(
          `/api/personal-room/status?roomSlug=${encodeURIComponent(roomSlug)}`,
        )
        const data = (await res.json()) as {
          active?: boolean
          ownerDid?: string | null
          ownerHandle?: string | null
        }
        if (cancelled) return
        if (!data.active) {
          setRole('no-session')
          return
        }
        setOwnerHandle(data.ownerHandle ?? null)
        if (
          isAuthenticated &&
          currentAccount?.did &&
          data.ownerDid === currentAccount.did
        ) {
          setRole('owner')
        } else {
          setRole('guest')
        }
      } catch (err) {
        if (cancelled) return
        console.error('status lookup failed:', err)
        setRole('no-session')
      }
    }
    resolveRole()
    return () => {
      cancelled = true
    }
  }, [isEntryRoute, roomSlug, isAuthenticated, currentAccount?.did])

  // Get or create a persistent guest ID for returning guest identification
  const getGuestId = useCallback(() => {
    if (typeof window === 'undefined') return null
    let guestId = localStorage.getItem('batesky_guest_id')
    if (!guestId) {
      guestId = `guest-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
      localStorage.setItem('batesky_guest_id', guestId)
    }
    return guestId
  }, [])

  useEffect(() => {
    if (role === 'guest') {
      getGuestId()
    }
  }, [role, getGuestId])

  const fetchInviteCode = useCallback(
    (sid?: string | null) => {
      const id = sid || sessionId
      if (!id || !roomSlug) return
      const guestId = getGuestId()
      fetch('/api/personal-room/invite-code', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({roomSlug, sessionId: id, guestId}),
      })
        .then(res => res.json())
        .then(data => {
          if (data.code && typeof window !== 'undefined') {
            localStorage.setItem('batesky_invite_code', data.code)
          }
        })
        .catch(() => {})
    },
    [roomSlug, sessionId, getGuestId],
  )

  const handleOwnerLeft = useCallback(
    (sid?: string) => {
      setRoomEnded('owner_left')
      fetchInviteCode(sid || sessionId)
    },
    [fetchInviteCode, sessionId],
  )

  const handleGuestLeft = useCallback(() => {
    setRoomEnded('guest_left')
    fetchInviteCode(sessionId)
  }, [fetchInviteCode, sessionId])

  const handleSessionId = useCallback((sid: string) => {
    setSessionId(sid)
  }, [])

  const handleRejoin = useCallback(() => {
    setRoomEnded(false)
  }, [])

  // Owner: register beforeunload deactivation. Activation already happened
  // server-side when start-session was called; we only need to clean up.
  useEffect(() => {
    if (role !== 'owner' || !roomSlug) return

    const deactivate = () => {
      if (deactivatedRef.current) return
      deactivatedRef.current = true
      const body = JSON.stringify({roomSlug})
      if (navigator.sendBeacon) {
        navigator.sendBeacon(
          '/api/personal-room/deactivate',
          new Blob([body], {type: 'application/json'}),
        )
      } else {
        fetch('/api/personal-room/deactivate', {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body,
          keepalive: true,
        }).catch(() => {})
      }
    }

    window.addEventListener('beforeunload', deactivate)
    return () => {
      deactivate()
      window.removeEventListener('beforeunload', deactivate)
    }
  }, [role, roomSlug])

  // Hide all shell UI when in a call
  useEffect(() => {
    if (isEntryRoute) return
    setMinimalShell(true)

    const style = document.createElement('style')
    style.id = 'personal-room-styles'
    style.innerHTML = `
      [role="navigation"],
      [style*="position: fixed"][style*="left: 50%"],
      [style*="position: fixed"][style*="bottom: 0"],
      [style*="position: fixed"][style*="top: 0"],
      [data-testid="chat-bubble-root"] {
        display: none !important;
        opacity: 0 !important;
        pointer-events: none !important;
        visibility: hidden !important;
      }
    `
    document.head.appendChild(style)

    return () => {
      setMinimalShell(false)
      document.getElementById('personal-room-styles')?.remove()
    }
  }, [isEntryRoute, setMinimalShell])

  const handleClose = useCallback(() => {
    if (role === 'owner') {
      if (!deactivatedRef.current) {
        deactivatedRef.current = true
        fetch('/api/personal-room/deactivate', {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({roomSlug}),
        }).catch(() => {})
      }
      navigation.goBack()
    } else {
      handleGuestLeft()
    }
  }, [role, roomSlug, navigation, handleGuestLeft])

  // Entry route: show a spinner while we mint a session, or an error.
  if (isEntryRoute) {
    return (
      <View style={[styles.container, {paddingTop: safeAreaInsets.top}]}>
        <View style={styles.endedContainer}>
          {entryError ? (
            <Text style={styles.errorText}>{entryError}</Text>
          ) : (
            <>
              <ActivityIndicator size="large" color="#fd7c21" />
              <Text style={styles.loadingText}>Starting your room…</Text>
            </>
          )}
        </View>
      </View>
    )
  }

  if (roomEnded) {
    return (
      <View style={[styles.container, {paddingTop: safeAreaInsets.top}]}>
        <View style={styles.endedContainer}>
          {roomEnded === 'guest_left' && (
            <Pressable
              accessibilityRole="button"
              style={styles.rejoinButton}
              onPress={handleRejoin}>
              <Text style={styles.rejoinButtonText}>Rejoin Room</Text>
            </Pressable>
          )}

          {role !== 'owner' && (
            <Pressable
              accessibilityRole="button"
              style={styles.signUpButton}
              onPress={() => {
                window.open('https://batesky.app', '_blank')
              }}>
              <Text style={styles.signUpButtonText}>Join Batesky</Text>
            </Pressable>
          )}
        </View>
      </View>
    )
  }

  if (role === 'pending') {
    return (
      <View style={[styles.container, {paddingTop: safeAreaInsets.top}]}>
        <View style={styles.endedContainer}>
          <ActivityIndicator size="large" color="#fd7c21" />
          <Text style={styles.loadingText}>Loading room…</Text>
        </View>
      </View>
    )
  }

  if (role === 'no-session') {
    return (
      <View style={[styles.container, {paddingTop: safeAreaInsets.top}]}>
        <View style={styles.endedContainer}>
          <Text style={styles.errorText}>
            This 1:1 room link is no longer active.
          </Text>
        </View>
      </View>
    )
  }

  const ownerDisplay = ownerHandle ? stripBskySuffix(ownerHandle) : 'Host'

  return (
    <View style={[styles.container, {paddingTop: safeAreaInsets.top}]}>
      {role === 'owner' ? (
        <JitsiMeeting
          roomId={roomSlug}
          roomName={`${stripBskySuffix(currentAccount?.handle || '')}'s Room`}
          roomTags={[]}
          onClose={handleClose}
          onRecordingDetected={() => {}}
        />
      ) : (
        <JitsiGuestMeeting
          roomSlug={roomSlug}
          ownerUsername={ownerDisplay}
          onClose={handleClose}
          onOwnerLeft={handleOwnerLeft}
          onSessionId={handleSessionId}
        />
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    backgroundColor: '#000',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
  endedContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
    maxWidth: 400,
    alignSelf: 'center',
    width: '100%',
  },
  rejoinButton: {
    backgroundColor: '#fd7c21',
    paddingVertical: 12,
    paddingHorizontal: 32,
    borderRadius: 8,
    marginBottom: 32,
  },
  rejoinButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  signUpButton: {
    backgroundColor: '#fd7c21',
    paddingVertical: 12,
    paddingHorizontal: 32,
    borderRadius: 8,
  },
  signUpButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  loadingText: {
    color: '#fff',
    fontSize: 16,
    marginTop: 16,
    textAlign: 'center',
  },
  errorText: {
    color: '#ff6b6b',
    fontSize: 16,
    textAlign: 'center',
  },
})
